import { useRef, useState, useEffect } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { ERROR_CODES } from '@marketplace/api';
import { DEFINITION_ROUTE_DEVELOPER_DASHBOARD_APPLICATION } from '@developer/constants';
import { Completion } from '../completion';

import classes from './add-edit-app-form/add-edit-app-form.module.scss';

import { AddEditApplicationForm } from './add-edit-app-form/add-edit-app-form';
import {
  TAddApplicationFormFields,
  TAddApplicationModalState,
  TAddApplicationModalProps,
  TAddApplicationFormData,
} from './add-edit-app-form/add-edit-app-form.types';

import { Dialog } from '@marketplace/ui-elements';
import { addApplicationFormValidationSchema } from './add-edit-app-form/add-edit-app-form.validation';
import { joiResolver } from '@hookform/resolvers/joi';
import useAddEditApplicationModal from './add-edit-app-modal.hook';

type TUrlParams = {
  id: string;
};
export const AddEditApplicationModal = (props: TAddApplicationModalProps): JSX.Element => {
  const { formatMessage }: IntlShape = useIntl();
  const { id: applicationId } = useParams<TUrlParams>();

  const {
    postLoading,
    postData,
    postDataErrors,
    putLoading,
    putData,
    putDataErrors,
    postError,
    putError,
    fetchPostApplication,
    fetchPutApplication,
    convertDataForForm,
  } = useAddEditApplicationModal(applicationId, props.application);

  const { push } = useHistory();

  const formRef = useRef<HTMLFormElement>(null);
  const [isEditModeOn, setIsEditModeOn] = useState(false);
  const [isSaveFailed, setIsSaveFailed] = useState(false);
  const closeErrorCompletionModal = () => {
    setIsSaveFailed(false);
  };

  const [modalState, setModalState] = useState<TAddApplicationModalState>(
    props.isUpdate
      ? {
          categories: props.application?.categories ?? [],
          name: props.application?.name ?? '',
          products: props.application?.products ?? [],
          facing: props.application?.facing ?? 'PROVIDER',
          short_description: props.application?.short_description ?? '',
          intendedUsers: props.application?.intendedUsers ?? [],
          intendedPurposes: props.application?.intendedPurposes ?? [],
        }
      : {
          categories: undefined,
          name: '',
          products: undefined,
          facing: 'PROVIDER',
          short_description: '',
          intendedUsers: undefined,
          intendedPurposes: undefined,
        }
  );

  useEffect(() => {
    if (putData && props.setApplication) {
      props.setApplication(putData);
      props.onReject();
    }
  }, [putData]);

  const onSubmit = (data: any) => {
    props.isUpdate ? fetchPutApplication(data) : fetchPostApplication(data);
  };

  useEffect(() => {
    reset(
      convertDataForForm({
        categories: props.application?.categories ?? [],
        name: props.application?.name ?? '',
        products: props.application?.products ?? [],
        short_description: props.application?.short_description ?? '',
        facing: props.application?.facing ?? 'PROVIDER',
        intendedUsers: props.application?.intendedUsers ?? [],
        intendedPurposes: props.application?.intendedPurposes ?? [],
      })
    );
  }, [props.application]);

  useEffect(() => {
    if (postData?.id) {
      push(`${DEFINITION_ROUTE_DEVELOPER_DASHBOARD_APPLICATION}${postData.id}`);
    }
  }, [postData]);

  const { handleSubmit, formState, control, getValues, reset, setError, setValue } = useForm<TAddApplicationFormData>({
    mode: 'onChange',
    defaultValues: modalState,
    shouldUnregister: true,
    resolver: joiResolver(addApplicationFormValidationSchema({ formatMessage })),
  });

  useEffect(() => {
    if (isEditModeOn) {
      modalState.intendedUsers =
        props.application &&
        props.application?.intendedUsers
          ?.filter((item: { name: string; id: string }) => item.name != 'Unknown')
          .map((item: { name: string; id: string }) => item.id);
      modalState.intendedPurposes =
        props.application &&
        props.application?.intendedPurposes
          ?.filter((item: { name: string; id: string }) => item.name != 'Unknown')
          .map((item: { name: string; id: string }) => item.id);
      
      if (modalState.intendedUsers == undefined || modalState.intendedUsers.length == 0) {
        setError('intendedUsers', {
          message: formatMessage(
            { id: 'generalForm_validation_required' },
            {
              field: formatMessage({
                id: 'addApplicationModal_intendedUserTitle',
              }),
            }
          ),
        });
      }
      if (modalState.intendedPurposes == undefined || modalState.intendedPurposes.length == 0) {
        setError('intendedPurposes', {
          message: formatMessage(
            { id: 'generalForm_validation_required' },
            {
              field: formatMessage({
                id: 'addApplicationModal_intendedPurposeTitle',
              }),
            }
          ),
        });
      }
    }
  }, [isEditModeOn]);

  useEffect(() => {
    if (props.isOpen && props.isUpdate) {
      setIsEditModeOn(true);
    } else {
      setIsEditModeOn(false);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (
      postDataErrors?.find((item: any) => item.code === ERROR_CODES.APP_NAME_UNIQUES) ||
      putDataErrors?.find((item: any) => item.code === ERROR_CODES.APP_NAME_UNIQUES)
    ) {
      setError('name', {
        message: formatMessage(
          { id: 'generalForm_validation_unique' },
          {
            field: formatMessage({
              id: 'addApplicationModal_applicationName',
            }),
          }
        ),
      });
    }
  }, [postDataErrors, putDataErrors]);

  useEffect(() => {
    if (postError || putError) {
      setIsSaveFailed(true);
      props.onReject();
    }
  }, [postError,putError]);

  const isAllRequiredFieldsFilled = 
    Object.entries(formState.dirtyFields).length >= 4 &&
    getValues('products') != undefined && getValues('products').length !== 0 &&
    getValues('categories') != undefined && getValues('categories').length !== 0;

  const isDataChanged = Object.entries(formState.dirtyFields).length !== 0;
  const isValidForm = formState.isValid;

  const onAcceptLabel = props.isUpdate
    ? formatMessage({
        id: 'addApplicationModal_saveAppButton',
      })
    : formatMessage({
        id: 'addApplicationModal_addAppButton',
      });

  const triggerSubmitForm = (): void => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };
  const handleChangeForm = (fields: TAddApplicationFormFields): void => {
    setModalState((prevState) => ({
      ...prevState,
      ...fields,
    }));
  };
  const formProps = {
    formRef,
    hidden: false,
    initialValues: modalState,
    onChange: handleChangeForm,
    onSubmit,
    handleSubmit,
    control,
    formState,
    fetchPostApplication,
    setValue,
  };

  return (
    <div>
      <Dialog
        visible={props.isOpen}
        isAcceptDisable={props.isUpdate ? !isDataChanged || !isValidForm : !isAllRequiredFieldsFilled || !isValidForm}
        isLoading={postLoading || putLoading}
        closable
        rootClassName={classes.root}
        header={
          props.isUpdate
            ? formatMessage({ id: 'addApplicationModal_header_edit' })
            : formatMessage({ id: 'addApplicationModal_header_new' })
        }
        previousLabel={formatMessage({
          id: 'registrationModalFeature_prevButton',
        })}
        rejectLabel={formatMessage({
          id: 'addApplicationModal_cancelButton',
        })}
        acceptLabel={onAcceptLabel}
        onReject={props.onReject}
        onAccept={triggerSubmitForm}
      >
        <AddEditApplicationForm
          setApplication={props.setApplication}
          application={props.application}
          isOpen={props.isOpen}
          onReject={props.onReject}
          {...formProps}
        />
      </Dialog>
      <Dialog
        visible={isSaveFailed}
        header={formatMessage({ id: 'addApplicationModal_save_functionality_failed' })}
        acceptLabel={''}
        rejectLabel={''}
        onClick={closeErrorCompletionModal}
      >
        <Completion completionText={formatMessage({ id: 'addApplicationModal_save_failed_message' })} />
      </Dialog>
    </div>
  );
};

AddEditApplicationModal.defaultProps = {
  isOpen: false,
  isUpdate: false,
};
